/**
 * SuggestionMark — TipTap mark extension for inline tracked changes
 *
 * Wraps text with visual indicators:
 * - type='deletion': red strikethrough (original text to be replaced)
 * - type='addition': green underline (proposed replacement text)
 *
 * Both share the same suggestionId for grouping.
 */

import { Mark, mergeAttributes } from '@tiptap/core';

const SuggestionMark = Mark.create({
  name: 'suggestion',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      suggestionId: { default: null },
      type: { default: 'addition' }, // 'deletion' | 'addition'
      authorName: { default: '' },
      status: { default: 'pending' }, // 'pending' | 'voting' | 'accepted' | 'rejected'
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-suggestion-id]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const type = HTMLAttributes.type || 'addition';
    const status = HTMLAttributes.status || 'pending';

    const classes = [];
    if (status === 'accepted' || status === 'rejected') {
      classes.push('suggestion-resolved');
    } else if (type === 'deletion') {
      classes.push('suggestion-deletion');
    } else {
      classes.push('suggestion-addition');
    }

    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, {
        'data-suggestion-id': HTMLAttributes.suggestionId,
        'data-suggestion-type': type,
        'data-suggestion-status': status,
        'data-suggestion-author': HTMLAttributes.authorName,
        class: classes.join(' '),
        title: `${type === 'deletion' ? 'Remove' : 'Add'}: by ${HTMLAttributes.authorName}`,
      }),
      0,
    ];
  },

  // Don't extend mark when typing at boundaries
  inclusive() {
    return false;
  },
});

export default SuggestionMark;

/**
 * CSS to inject for suggestion marks:
 *
 * .suggestion-deletion {
 *   text-decoration: line-through;
 *   background-color: #fee2e2;
 *   color: #991b1b;
 *   border-radius: 2px;
 *   padding: 0 2px;
 * }
 * .suggestion-addition {
 *   background-color: #dcfce7;
 *   color: #166534;
 *   border-bottom: 2px solid #22c55e;
 *   border-radius: 2px;
 *   padding: 0 2px;
 * }
 * .suggestion-resolved {
 *   opacity: 0.4;
 * }
 */
